import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  IconButton,
  Breadcrumbs,
  Link
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import Main from '../layouts/Main';
import { useParams, useNavigate } from 'react-router-dom';
import ImageGallery from '../components/ImageGallery';
import AddImageDialog from '../components/AddImageDialog';
import EditImageDialog from '../components/EditImageDialog';
import ImagePreviewDialog from '../components/ImagePreviewDialog';

const ProductImagesDetail = () => {
  const { sku } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [images, setImages] = useState([]);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const fetchImagesBySKU = async () => {
    try {
      setLoading(true);
      const response = await fetch(`https://elnaf.fi/apiElnaf/api/get_product_images.php?sku=${encodeURIComponent(sku)}`);
      const data = await response.json();
      console.log('SKU images response:', data);
      
      // Transform the response to match our expected format
      const transformedImages = data.map((img, index) => ({
        ...img,
        image_url: img.image_url || img.url, // Handle both possible field names
        id: img.id || `${sku}-${index}` // Ensure we have an ID
      }));
      
      setImages(transformedImages);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching images for SKU:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (sku) {
      fetchImagesBySKU();
    }
  }, [sku]);

  const handleEditClick = (image) => {
    setSelectedImage(image);
    setEditDialogOpen(true);
  };

  const handlePreviewClick = (image) => {
    setSelectedImage(image);
    setPreviewDialogOpen(true);
  };

  const handleCloseDialogs = () => {
    setAddDialogOpen(false);
    setEditDialogOpen(false);
    setPreviewDialogOpen(false);
    setSelectedImage(null);
  };

  const handleSave = async () => {
    handleCloseDialogs();
    await fetchImagesBySKU();
  };

  const handleDragEnd = async (newImages) => {
    // Update the order in the API
    try {
      const updatePromises = newImages.map(async (image) => {
        const response = await fetch('https://elnaf.fi/apiElnaf/api/update_product_image.php', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: image.id,
            sort_order: image.sort_order
          })
        });
        return response.json();
      });
      
      await Promise.all(updatePromises);
      console.log('Sort orders updated successfully');
      await fetchImagesBySKU();
    } catch (error) {
      console.error('Error updating sort orders:', error);
    }
  };

  const handleAddImage = () => {
    // Pre-fill the SKU in the add dialog
    setSelectedImage({ sku: sku });
    setAddDialogOpen(true);
  };

  return (
    <Main>
      <div className="page-header">
        <div>
          <h1 className="page-title">
            <IconButton onClick={() => navigate('/product-images')} sx={{ mr: 1 }}>
              <ArrowBackIcon />
            </IconButton>
            Images for {sku}
          </h1>
          <Breadcrumbs aria-label="breadcrumb" sx={{ mt: 1, ml: 6 }}>
            <Link
              underline="hover"
              color="inherit"
              href="/"
              onClick={(e) => {
                e.preventDefault();
                navigate('/');
              }}
            >
              Home
            </Link>
            <Link
              underline="hover"
              color="inherit"
              href="/product-images"
              onClick={(e) => {
                e.preventDefault();
                navigate('/product-images');
              }}
            >
              Product Images
            </Link>
            <Typography color="text.primary">{sku}</Typography>
          </Breadcrumbs>
        </div>
        <div className="page-header-actions">
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddImage}
            sx={{ 
              backgroundColor: '#845adf',
              '&:hover': {
                backgroundColor: '#6c48c5'
              }
            }}
          >
            Add Image
          </Button>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="card custom-card">
            <div className="card-header">
              <div className="card-title">Manage Images</div>
              <div className="ms-auto">
                <Typography variant="body2" color="text.secondary">
                  Total: {images.length} images
                </Typography>
              </div>
            </div>
            <div className="card-body">
              {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                  <CircularProgress />
                </Box>
              ) : images.length > 0 ? (
                <ImageGallery
                  images={images}
                  onEdit={handleEditClick}
                  onPreview={handlePreviewClick}
                  onDragEnd={handleDragEnd}
                  onRefresh={fetchImagesBySKU}
                />
              ) : (
                <Box textAlign="center" py={5}>
                  <Typography variant="h6" color="text.secondary">
                    No images found for this SKU. Add your first image!
                  </Typography>
                </Box>
              )}
            </div>
          </div>
        </div>
      </div>

      <AddImageDialog
        open={addDialogOpen}
        onClose={handleCloseDialogs}
        onSave={handleSave}
        defaultSKU={sku}
      />

      <EditImageDialog
        open={editDialogOpen}
        onClose={handleCloseDialogs}
        image={selectedImage}
        onSave={handleSave}
      />

      <ImagePreviewDialog
        open={previewDialogOpen}
        onClose={handleCloseDialogs}
        image={selectedImage}
      />
    </Main>
  );
};

export default ProductImagesDetail;